import { Button, Empty, Spin, Table, Tag, Typography } from 'antd';
import type { ColumnsType } from 'antd/es/table';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';

import type { WorkflowSummary } from '@/api/workflows';
import { statusColor } from '@/originator/statusUtils';

const { Text } = Typography;

/**
 * Items + loading are owned by ApproverDashboard so the tab title can show a
 * count without an extra fetch and so a future "refresh all" affordance can
 * trigger a single reload across every tab.
 */
type Props = {
  items: WorkflowSummary[] | null;
  loading: boolean;
  /** Current search needle so the empty-state can distinguish "queue truly
   *  empty" from "search matched nothing on this tab" (INFLOWS-107 AC03). */
  searchQuery?: string;
  /** Clear-search callback the empty-state renders behind a button when
   *  searchQuery is non-empty. */
  onClearSearch?: () => void;
};

export function PendingQueue({ items, loading, searchQuery = '', onClearSearch }: Props) {
  const { t, i18n } = useTranslation();
  const navigate = useNavigate();

  const columns: ColumnsType<WorkflowSummary> = [
    {
      title: t('workflow.fields.appName'),
      dataIndex: 'appName',
      key: 'appName',
      render: (v: string, row) => (
        // Was a bare <a> with an onClick and no href: not focusable, not
        // announced as a control, unreachable by keyboard (WCAG 2.1.1). AntD's
        // link-styled Button renders a real <button> and looks identical.
        <Button
          type="link"
          style={{ padding: 0, height: 'auto' }}
          onClick={() => navigate(`/approver/workflows/${row.id}`)}
        >
          {v}
        </Button>
      ),
    },
    {
      title: t('workflow.fields.name'),
      key: 'name',
      render: (_, row) => (i18n.language.startsWith('fr') ? row.nameFr : row.nameEn),
    },
    {
      // Same New Form / Change semantics the Originator's cards use. On the
      // Pending Queue every row is the in-flight version being reviewed, so
      // currentVersionNumber === 0 → "New Form" (v1 in flight, workflow has
      // never been approved), ≥ 1 → "Change" (v2+ in flight, updating an
      // already-approved form). Lets the approver see at a glance whether
      // they're vetting a brand-new form or a delta to an existing one.
      title: t('workflow.fields.requestType', { defaultValue: 'Request Type' }),
      key: 'requestType',
      width: 130,
      render: (_, row) => {
        const isNew = row.currentVersionNumber === 0;
        return (
          <Tag
            color={isNew ? 'blue' : 'gold'}
            style={{ marginInlineEnd: 0, fontWeight: 600, letterSpacing: 0.2 }}
          >
            {isNew
              ? t('workflow.list.requestTypeNewForm', { defaultValue: 'New Form' })
              : t('workflow.list.requestTypeChange', { defaultValue: 'Change' })}
          </Tag>
        );
      },
    },
    {
      title: t('workflow.fields.type'),
      dataIndex: 'type',
      key: 'type',
    },
    {
      // Snapshot of the person who authored + submitted the workflow, taken at
      // CreateDraft time. Empty for legacy rows (pre-OwnerName) — fall back to
      // the opaque `ownerOid` so the column is never blank.
      title: t('workflow.fields.originator', { defaultValue: 'Originator' }),
      key: 'originator',
      render: (_, row) => (
        <Text>{row.ownerName?.trim() ? row.ownerName : row.ownerOid || '—'}</Text>
      ),
    },
    {
      title: t('workflow.fields.status'),
      dataIndex: 'status',
      key: 'status',
      render: (s: WorkflowSummary['status']) => <Tag color={statusColor(s)}>{s}</Tag>,
    },
    {
      // Prefer submittedAt (real submit moment) over updatedAt (drifts on autosave
      // + on approve/reject writes). Legacy rows written before submittedAt landed
      // fall back to updatedAt so the column never shows "—" for pre-2026-07-11
      // rows still in the queue.
      title: t('workflow.fields.submittedAt', { defaultValue: 'Submitted' }),
      key: 'submittedAt',
      render: (_, row) => {
        const iso = row.submittedAt ?? row.updatedAt;
        return iso ? new Date(iso).toLocaleString(i18n.language) : '—';
      },
    },
    {
      title: t('workflow.fields.actions'),
      key: 'actions',
      render: (_, row) => (
        <Button
          size="small"
          type="primary"
          onClick={() => navigate(`/approver/workflows/${row.id}`)}
        >
          {t('approver.queue.review')}
        </Button>
      ),
    },
  ];

  if (loading && items === null) {
    return (
      <div style={{ display: 'flex', justifyContent: 'center', padding: 32 }}>
        <Spin size="large" />
      </div>
    );
  }

  if (items && items.length === 0) {
    // Search-aware empty state (INFLOWS-107 AC03): distinguish "the queue is
    // truly empty" from "nothing matches the current search on the Pending
    // tab" and offer a one-click Clear-search escape hatch when relevant.
    const needle = searchQuery.trim();
    return (
      <Empty
        description={
          needle
            ? t('workflow.list.noneMatchSearch', {
                query: needle,
                defaultValue: 'No workflows match "{{query}}" on this tab.',
              })
            : t('approver.queue.empty')
        }
      >
        {needle && onClearSearch && (
          <Button onClick={onClearSearch}>
            {t('workflow.list.clearSearch', { defaultValue: 'Clear search' })}
          </Button>
        )}
      </Empty>
    );
  }

  return (
    <Table<WorkflowSummary>
      rowKey="id"
      columns={columns}
      dataSource={items ?? []}
      loading={loading}
      pagination={{ pageSize: 20 }}
      size="small"
    />
  );
}
